import React, { createContext, useState, useEffect } from "react";
import { arrayToOption } from "../../Utils/formatUtils";
import useFetch from "../../hooks/useFetch";

export const ProductsContext = createContext();
ProductsContext.displayName = "Products List Context";

export const ProductsContextProvider = (props) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [maxPagination, setMaxPagination] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [latestQuery, setLatestQuery] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [suppliersOptions, setSuppliersOptions] = useState([]);
  const { get, post, del } = useFetch();

  useEffect(() => {
    const fetchData = async () => {
      getCategoriesOptions();
      getSuppliersOptions();
      getBrandsOptions();
      getProducts();
    };
    fetchData();
  }, []);

  const getProducts = async (queries = {}) => {
    setIsLoading(true);
    if (!queries.limit) {
      queries.limit = 50000000;
    }
    setLatestQuery(queries);
    let url = "/products?";

    Object.keys(queries).map(function (key, index) {
      url += key + "=" + queries[key] + "&";
    });

    get(url)
      .then((res) => {
        setProducts(res.products);
        setMaxPagination(res.total);
        setCurrentPage(Number(res.page));
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const setPagination = (page) => {
    let newQuery = { ...latestQuery };
    newQuery.page = page;
    getProducts(newQuery);
  };

  const setOrderKey = (key, direction) => {
    let newQuery = { ...latestQuery };
    newQuery.orderBy = key;
    newQuery.orderDirection = direction;
    getProducts(newQuery);
  };

  const getCategoriesOptions = () => {
    get("/products/properties/categories")
      .then((res) => {
        setCategoriesOptions(arrayToOption(res));
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getSuppliersOptions = () => {
    get("/suppliers")
      .then((res) => {
        setSuppliersOptions(arrayToOption(res, "id", "businessName"));
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getBrandsOptions = () => {
    get("/products/properties/brands")
      .then((res) => {
        setBrandsOptions(arrayToOption(res));
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const addCategory = (newCategory) => {
    return new Promise((resolve, reject) => {
      post("/products/properties/categories", { name: newCategory })
        .then(async () => {
          await getCategoriesOptions();
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const deleteCategory = (categoryId) => {
    return new Promise((resolve, reject) => {
      del(`/products/properties/categories/${categoryId}`)
        .then(async () => {
          await getCategoriesOptions();
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const addBrand = (newBrand) => {
    return new Promise((resolve, reject) => {
      post("/products/properties/brands", { name: newBrand })
        .then(async () => {
          await getBrandsOptions();
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const deleteBrand = (brandId) => {
    return new Promise((resolve, reject) => {
      del(`/products/properties/brands/${brandId}`)
        .then(async () => {
          await getBrandsOptions();
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const deleteProduct = (productId) => {
    return new Promise((resolve, reject) => {
      del(`/products/${productId}`)
        .then(async () => {
          await getProducts();
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  return (
    <ProductsContext.Provider
      value={{
        products,
        currentPage,
        maxPagination,
        setPagination,
        setProducts,
        getProducts,
        selectedProducts,
        setSelectedProducts,
        setOrderKey,
        brandsOptions,
        addBrand,
        deleteBrand,
        categoriesOptions,
        suppliersOptions,
        addCategory,
        deleteProduct,
        deleteCategory,
        isLoadingProduct: isLoading,
      }}
    >
      {props.children}
    </ProductsContext.Provider>
  );
};
